import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@ctct/payflow-ui';
import { Alert } from '@ctct/presentation-layer';

import type { PaymentMethodsLoadedProps } from '../widgets/PaymentMethodsWidget/types';

interface Props {
  data: Pick<PaymentMethodsLoadedProps['data'], 'getAmazonButtonId'>;
  handlers: Pick<PaymentMethodsLoadedProps['handlers'], 'renderAmazonButton' | 'handleAmazonToken'>;
  hooks: Pick<PaymentMethodsLoadedProps['hooks'], 'useAmazonPayConnected'>;
}

export const AmazonPay = ({ data, handlers, hooks }: Props) => {
  const { t } = useTranslation();

  const amazonPayConnected = hooks.useAmazonPayConnected();

  // TODO: implement once we connect it to payment services
  // after we send a request to `/plan/pay`
  // if we have an amazon token and response.ok == false - we assume that payment is declined
  const amazonPayPaymentDeclined = false;

  useEffect(() => {
    handlers.renderAmazonButton();
    handlers.handleAmazonToken();

    // perform above actions only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {amazonPayPaymentDeclined && (
        <Alert variant="warning" message={t('payment-methods.amazon_pay_payment_declined')} />
      )}
      <p>{t('payment-methods.amazon_description')}</p>
      <div className="amazon-pay-button-container">
        {amazonPayConnected && (
          <div style={{ width: 'max-content' }}>
            <Button
              variant={amazonPayPaymentDeclined ? 'primary' : 'secondary'}
              data-qe-id="amazon-pay-update-button"
              onClick={() => {
                window.document.getElementById(data.getAmazonButtonId())?.click();
              }}
            >
              {t(
                amazonPayPaymentDeclined
                  ? 'payment-methods.amazon_update_from_declined'
                  : 'payment-methods.amazon_update'
              )}
            </Button>
          </div>
        )}

        <div
          style={{ display: amazonPayConnected ? 'none' : 'auto' }}
          aria-label="amazon button"
          id={data.getAmazonButtonId()}
          data-qe-id="amazon-pay-button"
          role="button"
          tabIndex={0}
        />
      </div>
    </>
  );
};
